/**
 * FROZEN legacy tag list (forecast areas spec, 2 Oct 2026, section 10.8).
 *
 * These 20 regions survive only as tags for the forum, events, signup intent and the
 * Companion down-day guides (and `companion_bases.region_slug`). They are not a public region
 * vocabulary and no page is built from them. Use `forecast_areas` (lib/areas/catalog.ts) for
 * anything new; a Companion base finds its area through its anchor spot.
 *
 * Membership rules, checked by lib/today/__tests__/regions.test.ts and
 * scripts/check-today-regions.ts: no spot in two regions, no sailing venue in a coast region,
 * mountain regions hold only ski spots, and every spot sits within 120 km (mountain) or 150 km
 * (coast) of its region anchor unless listed in TODAY_REGION_DISTANCE_EXCEPTIONS.
 */
export type TodayRegion = {
  slug: string;
  name: string;
  tagline: string;
  countryCodes: string[];
  anchor: { lat: number; lng: number };
  timezone: string;
  locationSlugs: string[];
  kind: "mountain" | "coast" | "mixed";
};

export const TODAY_REGIONS: TodayRegion[] = [
  {
    slug: "portes-du-soleil",
    name: "Portes du Soleil",
    tagline: "Linked Alpine laps from Morzine to Avoriaz.",
    countryCodes: ["FR", "CH"],
    anchor: { lat: 46.18, lng: 6.73 },
    timezone: "Europe/Paris",
    // Portes du Soleil resorts only. Chamonix and La Plagne are not in it.
    locationSlugs: ["morzine", "avoriaz", "les-gets", "chatel", "champery"],
    kind: "mountain",
  },
  {
    slug: "espace-killy",
    name: "Espace Killy",
    tagline: "High Tarentaise snow calls around Val d'Isere and Tignes.",
    countryCodes: ["FR"],
    anchor: { lat: 45.46, lng: 6.94 },
    timezone: "Europe/Paris",
    locationSlugs: ["val-disere", "tignes", "sainte-foy", "la-rosiere"],
    kind: "mountain",
  },
  {
    slug: "three-valleys",
    name: "Les 3 Vallees",
    tagline: "Big lift-served days across Courchevel, Meribel and Val Thorens.",
    countryCodes: ["FR"],
    anchor: { lat: 45.37, lng: 6.59 },
    timezone: "Europe/Paris",
    locationSlugs: ["meribel", "courchevel", "val-thorens", "les-menuires"],
    kind: "mountain",
  },
  {
    slug: "chamonix-valley",
    name: "Chamonix valley",
    tagline: "Steep terrain, high weather windows and Mont Blanc options.",
    countryCodes: ["FR", "IT"],
    anchor: { lat: 45.92, lng: 6.87 },
    timezone: "Europe/Paris",
    locationSlugs: ["chamonix", "les-houches", "les-contamines", "saint-gervais", "megeve"],
    kind: "mountain",
  },
  {
    slug: "zermatt-cervinia",
    name: "Zermatt and Cervinia",
    tagline: "Glacier height and cross-border mountain weather.",
    countryCodes: ["CH", "IT"],
    anchor: { lat: 45.98, lng: 7.69 },
    timezone: "Europe/Zurich",
    locationSlugs: ["zermatt", "cervinia", "saas-fee", "monterosa"],
    kind: "mountain",
  },
  {
    slug: "arlberg",
    name: "Arlberg",
    tagline: "Austrian powder calls from St Anton to Lech.",
    countryCodes: ["AT"],
    anchor: { lat: 47.14, lng: 10.22 },
    timezone: "Europe/Vienna",
    locationSlugs: ["st-anton", "lech-zurs", "warth-schrocken"],
    kind: "mountain",
  },
  {
    slug: "tirol",
    name: "Tirol",
    tagline: "Central Austrian mountains with ski and bike windows.",
    countryCodes: ["AT"],
    anchor: { lat: 47.23, lng: 11.4 },
    timezone: "Europe/Vienna",
    locationSlugs: ["mayrhofen", "kitzbuhel", "solden"],
    kind: "mountain",
  },
  {
    slug: "dolomites",
    name: "Dolomites",
    tagline: "Italian high routes and sharp weather breaks.",
    countryCodes: ["IT"],
    anchor: { lat: 46.54, lng: 11.9 },
    timezone: "Europe/Rome",
    locationSlugs: ["cortina-dampezzo", "selva-val-gardena", "alta-badia", "arabba", "canazei", "kronplatz"],
    kind: "mountain",
  },
  {
    slug: "queenstown-wanaka",
    name: "Queenstown and Wanaka",
    tagline: "Southern Hemisphere winter from Remarkables to Treble Cone.",
    countryCodes: ["NZ"],
    anchor: { lat: -44.85, lng: 168.78 },
    timezone: "Pacific/Auckland",
    locationSlugs: ["queenstown-remarkables", "treble-cone", "cardrona", "coronet-peak", "ohau"],
    kind: "mountain",
  },
  {
    slug: "cornwall",
    name: "Cornwall",
    tagline: "Atlantic swell, storm havens and long beach-break choices.",
    countryCodes: ["GB"],
    anchor: { lat: 50.36, lng: -5.12 },
    timezone: "Europe/London",
    locationSlugs: [
      "newquay-fistral",
      "watergate-bay",
      "sennen",
      "porthtowan",
      "polzeath",
      "perranporth",
      "constantine-bay",
      "gwithian",
      "praa-sands",
      "porthleven",
      "harlyn-bay",
      "widemouth-bay",
      "bude-summerleaze",
      "crantock",
      "chapel-porth",
      "porthmeor",
      "lusty-glaze",
    ],
    kind: "coast",
  },
  {
    slug: "north-devon",
    name: "North Devon",
    tagline: "Wide Atlantic beaches with mellow and punchy options.",
    countryCodes: ["GB"],
    anchor: { lat: 51.16, lng: -4.23 },
    timezone: "Europe/London",
    locationSlugs: ["croyde", "woolacombe", "saunton", "putsborough"],
    kind: "coast",
  },
  {
    slug: "south-wales-gower",
    name: "South Wales and Gower",
    tagline: "Tidal reefs, beaches and estuary wind checks.",
    countryCodes: ["GB"],
    anchor: { lat: 51.58, lng: -4.35 },
    timezone: "Europe/London",
    locationSlugs: [
      "freshwater-west",
      "manorbier",
      "newgale",
      "whitesands",
      "llangennith",
      "caswell",
      "langland",
      "rest-bay",
    ],
    kind: "coast",
  },
  {
    slug: "south-west-france",
    name: "South West France",
    tagline: "Bay of Biscay sandbanks from Hossegor to Lacanau.",
    countryCodes: ["FR"],
    anchor: { lat: 43.9, lng: -1.3 },
    timezone: "Europe/Paris",
    locationSlugs: ["hossegor", "capbreton", "seignosse", "la-graviere", "les-cavaliers", "biarritz", "anglet", "guethary"],
    kind: "coast",
  },
  {
    slug: "basque-country",
    name: "Basque Country",
    tagline: "Urban beaches, reefs and river-mouth sandbars along the Basque coast.",
    countryCodes: ["ES", "FR"],
    anchor: { lat: 43.35, lng: -2.05 },
    timezone: "Europe/Madrid",
    locationSlugs: ["mundaka", "san-sebastian", "zarautz", "sopela", "deba", "getxo"],
    kind: "coast",
  },
  {
    slug: "west-portugal",
    name: "West Portugal",
    tagline: "Open Atlantic power from Nazare to Ericeira and Peniche.",
    countryCodes: ["PT"],
    anchor: { lat: 39.3, lng: -9.25 },
    timezone: "Europe/Lisbon",
    locationSlugs: ["nazare", "peniche-supertubos", "ericeira", "baleal", "ribeira-dilhas", "santa-cruz-portugal", "guincho", "carcavelos"],
    kind: "coast",
  },
  {
    slug: "algarve",
    name: "Algarve",
    tagline: "South-west Portugal options when Atlantic wind shifts.",
    countryCodes: ["PT"],
    anchor: { lat: 37.04, lng: -8.82 },
    timezone: "Europe/Lisbon",
    locationSlugs: ["sagres", "lagos", "arrifana", "amado"],
    kind: "coast",
  },
  {
    slug: "canary-islands",
    name: "Canary Islands",
    tagline: "Year-round Atlantic reefs and wind-exposed island breaks.",
    countryCodes: ["ES"],
    anchor: { lat: 28.55, lng: -15.85 },
    timezone: "Atlantic/Canary",
    locationSlugs: ["fuerteventura-cotillo", "lanzarote-famara", "tenerife", "gran-canaria-confital"],
    kind: "coast",
  },
  {
    slug: "morocco-atlantic",
    name: "Morocco Atlantic",
    tagline: "Moroccan points and sheltered right-hand walls.",
    countryCodes: ["MA"],
    anchor: { lat: 30.56, lng: -9.74 },
    timezone: "Africa/Casablanca",
    locationSlugs: ["taghazout", "imsouane", "anchor-point", "sidi-kaouki", "essaouira", "mirleft"],
    kind: "coast",
  },
  {
    slug: "bali",
    name: "Bali",
    tagline: "Bukit reef windows and warm Indian Ocean swell.",
    countryCodes: ["ID"],
    anchor: { lat: -8.82, lng: 115.09 },
    timezone: "Asia/Makassar",
    locationSlugs: ["uluwatu", "padang-padang", "keramas", "balangan", "bingin", "medewi"],
    kind: "coast",
  },
  {
    slug: "gold-coast",
    name: "Gold Coast",
    tagline: "Queensland point-break days from Snapper to Stradbroke.",
    countryCodes: ["AU"],
    anchor: { lat: -28.05, lng: 153.45 },
    timezone: "Australia/Brisbane",
    locationSlugs: ["snapper-rocks", "burleigh-heads", "kirra", "north-stradbroke"],
    kind: "coast",
  },
];

/** Coast regions may sit this far from their anchor; mountain regions 120 km. */
export const TODAY_REGION_MAX_KM = { mountain: 120, coast: 150, mixed: 150 } as const;

/** Named spots allowed beyond the distance rule (region:slug). Keep this list short. */
export const TODAY_REGION_DISTANCE_EXCEPTIONS: readonly string[] = Object.freeze([
  // The Canaries tag spans the islands; the anchor sits between Tenerife and Gran Canaria.
  "canary-islands:fuerteventura-cotillo",
  "canary-islands:lanzarote-famara",
]);

export type TodayRegionSpotFacts = Readonly<{ type: string; lat: number; lng: number }>;

/**
 * Pure membership check over known spot facts (seed or live). Returns one problem string per
 * broken rule; unknown slugs are reported too.
 */
export function todayRegionProblems(
  spots: ReadonlyMap<string, TodayRegionSpotFacts>,
  regions: readonly TodayRegion[] = TODAY_REGIONS,
): string[] {
  const problems: string[] = [];
  const owner = new Map<string, string>();
  const exceptions = new Set(TODAY_REGION_DISTANCE_EXCEPTIONS);
  for (const region of regions) {
    for (const slug of region.locationSlugs) {
      const previous = owner.get(slug);
      if (previous) problems.push(`${slug} is in both ${previous} and ${region.slug}`);
      else owner.set(slug, region.slug);
      const spot = spots.get(slug);
      if (!spot) {
        problems.push(`${region.slug}:${slug} is not a known spot`);
        continue;
      }
      const type = spot.type.toLowerCase();
      if (region.kind === "mountain" && type !== "ski") problems.push(`${region.slug}:${slug} is ${type} in a mountain region`);
      if (region.kind === "coast" && type === "sailing") problems.push(`${region.slug}:${slug} is a sailing venue in a coast region`);
      if (region.kind === "coast" && type === "ski") problems.push(`${region.slug}:${slug} is a ski resort in a coast region`);
      const km = haversineKm(region.anchor.lat, region.anchor.lng, spot.lat, spot.lng);
      if (km > TODAY_REGION_MAX_KM[region.kind] && !exceptions.has(`${region.slug}:${slug}`)) {
        problems.push(`${region.slug}:${slug} is ${Math.round(km)} km from the region anchor`);
      }
    }
  }
  return problems;
}

export function findTodayRegion(slug: string): TodayRegion | null {
  return TODAY_REGIONS.find((region) => region.slug === slug) ?? null;
}

export function nearestTodayRegion(lat: number, lng: number): TodayRegion | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  let nearest: { region: TodayRegion; distanceKm: number } | null = null;
  for (const region of TODAY_REGIONS) {
    const distanceKm = haversineKm(lat, lng, region.anchor.lat, region.anchor.lng);
    if (!nearest || distanceKm < nearest.distanceKm) {
      nearest = { region, distanceKm };
    }
  }

  return nearest && nearest.distanceKm <= 200 ? nearest.region : null;
}

export function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const earthRadiusKm = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadiusKm * Math.asin(Math.sqrt(a));
}

function toRad(value: number): number {
  return (value * Math.PI) / 180;
}
